/**
 * PresenceProvider — owns the current user's presence state.
 *
 * Composes:
 *   - useIdleDetector      (computes auto-status from activity)
 *   - useHeartbeat         (POST every 30s, multi-tab-deduped)
 *   - usePresenceStream    (subscribes to dashboard SSE — only when needed)
 *
 * Exposes via useContext(PresenceContext):
 *   { status, manualStatus, currentModule, others, setManualStatus, setCurrentModule }
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { useAuth } from './AuthProvider';
import useHeartbeat from '../hooks/useHeartbeat';
import useIdleDetector from '../hooks/useIdleDetector';
import usePresenceStream from '../hooks/usePresenceStream';
import api from '../api/api';

const PresenceContext = createContext({
  status: 'offline',
  manualStatus: null,
  currentModule: '',
  others: {},
  setManualStatus: () => {},
  setCurrentModule: () => {},
});

export const usePresence = () => useContext(PresenceContext);

export const PresenceProvider = ({ children }) => {
  const { token, userdetails, isAuthLoading } = useAuth();
  const authed = !!token && !isAuthLoading;

  const [manualStatus, setManualStatusLocal] = useState(null);
  const [currentModule, setCurrentModuleLocal] = useState('');
  const [others, setOthers] = useState({});

  const idleStatus = useIdleDetector();

  // Manual override beats auto unless cleared
  const computedStatus = manualStatus || idleStatus;
  const computedStatusRef = useRef(computedStatus);
  const currentModuleRef = useRef(currentModule);
  useEffect(() => { computedStatusRef.current = computedStatus; }, [computedStatus]);
  useEffect(() => { currentModuleRef.current = currentModule; }, [currentModule]);

  useHeartbeat({
    enabled: authed,
    getStatus: () => computedStatusRef.current,
    getCurrentModule: () => currentModuleRef.current,
  });

  // Hydrate manual_status from server on login
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    api.get('admin-monitoring/presence/me/')
      .then((res) => { if (!cancelled) setManualStatusLocal(res.data?.manual_status || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authed]);

  const setManualStatus = useCallback(async (newStatus) => {
    setManualStatusLocal(newStatus || null);
    try {
      await api.patch('admin-monitoring/presence/manual-status/', {
        manual_status: newStatus || null,
      });
    } catch {
      // best-effort; server will catch up on next heartbeat
    }
  }, []);

  const setCurrentModule = useCallback((m) => {
    setCurrentModuleLocal((m || '').slice(0, 50));
  }, []);

  // Dashboard-only stream subscription: only join when a monitoring page mounts.
  // For simplicity we always subscribe when authed AND the user is in a monitoring group.
  const inMonitoringGroup = useMemo(() => {
    const groups = userdetails?.group_names || [];
    return userdetails?.is_superuser
      || groups.includes('Administration / Monitoring Viewer')
      || groups.includes('Administration / Monitoring Manager')
      || groups.includes('Administration / Settings');
  }, [userdetails]);

  usePresenceStream(authed && inMonitoringGroup, {
    onPresenceUpdate: (evt) => {
      if (!evt?.user_id) return;
      setOthers((prev) => ({
        ...prev,
        [evt.user_id]: {
          status: evt.status,
          current_module: evt.current_module,
          last_seen_at: evt.last_seen_at,
          manual_status: evt.manual_status,
          // Receive time — consumers use this to ignore a stale live entry that
          // a newer REST poll has already superseded (a missed manual-status
          // event must not override the fresh polled value forever).
          ts: Date.now(),
        },
      }));
    },
    onForceLogout: (evt) => {
      if (evt?.user_id === userdetails?.id) {
        // Drop token and bounce — the user has been logged out remotely.
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        } catch { /* noop */ }
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
    },
  });

  const value = useMemo(() => ({
    status: computedStatus,
    manualStatus,
    currentModule,
    others,
    setManualStatus,
    setCurrentModule,
  }), [computedStatus, manualStatus, currentModule, others, setManualStatus, setCurrentModule]);

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export default PresenceProvider;
